import { Component, DestroyRef, inject, isDevMode, ChangeDetectionStrategy } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { AlertService } from 'src/app/services/alert.service';
import { Observable, of, map, startWith, switchMap, tap, catchError, EMPTY } from 'rxjs';
import { IOrganization } from 'src/app/models/organizations/organization';
import { IUserGroups, UserType } from 'src/app/models/users/userGroups';
import { getGroupsForUserType } from 'src/app/models/users/userGroups.helpers';
import { UserCreate } from 'src/app/models/users/userCreate';
import { ValidationErrors } from 'src/app/models/validationErrors';
import { OrganizationsService } from 'src/app/services/organizations.service';
import { UsersService } from 'src/app/services/users.service';
import { environment } from 'src/environments/environment';
import sha256 from 'crypto-js/sha256';
import { IUserView } from 'src/app/models/users/user-result';
import { timezoneList } from 'src/app/utils/timezone';
import { dateTimeFormat, defaultFormat } from 'src/app/utils/dateFormat';

@Component({
    selector: 'app-user-create',
    templateUrl: './user-create.component.html',
    styleUrls: ['./user-create.component.scss'],
    changeDetection: ChangeDetectionStrategy.Default,
    standalone: false
})
export class UserCreateComponent {
  UserType = UserType;
  private destroyRef = inject(DestroyRef);
  private fb = inject(FormBuilder);
  errorState: Boolean = false;
  timezoneListFiltered = timezoneList;
  dateTimeFormat = dateTimeFormat;

  nameFormControl = this.fb.nonNullable.control('', [Validators.required]);
  emailFormControl = this.fb.nonNullable.control('', [Validators.required]);
  pinFormControl = this.fb.nonNullable.control('', [Validators.pattern('^[0-9]*$')]);
  organizationFormControl = this.fb.nonNullable.control<IOrganization | string>('', [Validators.required]);
  preferenceForm = this.fb.nonNullable.group({
    timezone: [''],
    dateFormat: [defaultFormat],
    is24H: [true],
    asAgo: [true],
  });

  constructor(
    private usersService: UsersService,
    private organizationsService: OrganizationsService,
    public dialog: MatDialog,
    public dialogRef: MatDialogRef<UserCreateComponent>,
    private alertService: AlertService,
  ) {}

  newUser: UserCreate = new UserCreate();
  errorMessages: ValidationErrors[] = [];
  validatorProperties: string[] = [];
  userType: UserType = UserType.Unknown;
  organizations: IOrganization[] = [];
  organizationFilteredOptions!: Observable<IOrganization[]>;

  ngOnInit() {
    const initTz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const tz = timezoneList.find((el) => {
      const idx = el.utc.findIndex((tz) => tz === initTz);
      return idx > -1;
    });
    this.preferenceForm.patchValue({
      timezone: tz ? tz.text : '',
    });
  }

  onUserTypeChange() {
    if (this.userType === UserType.Tech || this.userType === UserType.Imagery) {
      this.pinFormControl.enable();
      this.pinFormControl.markAllAsTouched();
    } else if (this.userType === UserType.Client) {
      this.pinFormControl.disable();
      this.listOrganizations();
    } else {
      this.pinFormControl.disable();
    }
  }

  //Organizations List and filters
  listOrganizations() {
    if (this.organizations.length == 0) {
      this.organizationsService
        .listOrganizations()
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: (organizations: any) => {
            if (organizations) {
              this.organizations = organizations;
              if (isDevMode()) {
                console.log('[DEV] [UserCreateComponent] organizations found: ', organizations);
              }
              this.organizationFilteredOptions = this.organizationFormControl.valueChanges.pipe(
                startWith(''),
                map((value) => this.parseOrganizationValue(value)),
              );
            }
          },
          error: (error) => {
            console.error('[UserCreateComponent] error', error);
          },
        });
    }
  }
  private _filter(name: string): IOrganization[] {
    const filterValue = name.toLowerCase();
    return this.organizations.filter((option) => option.name.toLowerCase().includes(filterValue));
  }
  parseOrganizationValue(value: any) {
    const name = typeof value === 'string' ? value : value?.name;
    return name ? this._filter(name as string) : this.organizations.slice();
  }
  displayFn(option: IOrganization | null) {
    return option ? option.name : '';
  }

  //display validation messages
  validationMessage(property: string) {
    return this.errorMessages.find((error) => error.property == property)?.message;
  }

  userCreated: boolean = false;
  createdUser!: IUserView;

  createUser() {
    const createUser$ = this.userCreated
      ? of(this.createdUser)
      : this.createNewUser$();

    createUser$.pipe(
      switchMap((user) => {
        this.userCreated = true;
        this.createdUser = user;
        return this.updateGroups$();
      }),
      catchError(() => {
        this.errorState = true;
        return EMPTY;
      }),
      takeUntilDestroyed(this.destroyRef),
    ).subscribe(() => {
      this.dialogRef.close(true);
    });
  }

  private createNewUser$() {
    const { timezone, dateFormat, asAgo, is24H } = this.preferenceForm.getRawValue();
    const tz = timezoneList.find((el) => el.text === timezone);
    this.newUser = {
      name: this.nameFormControl.value,
      emailAddress: this.emailFormControl.value,
      preference: {
        timezone: tz!.utc[0],
        dateTimeFormat: dateFormat,
        asAgo,
        is24H,
      },
    };
    if (this.userType === UserType.Client) {
      const orgValue = this.organizationFormControl.value;
      this.newUser.organizationId = typeof orgValue !== 'string' ? orgValue?.id ?? undefined : undefined;
    }

    return this.usersService.createUser(this.newUser).pipe(
      map((response) => response.user),
      tap(() => this.alertService.successToast('Successfully created user profile')),
    );
  }

  private updateGroups$() {
    const requiresPin = this.userType === UserType.Tech || this.userType === UserType.Imagery;

    if (requiresPin && this.pinFormControl.value.length !== 4) {
      this.pinFormControl.markAllAsTouched();
      this.pinFormControl.setErrors({ serverErrors: 'validation errors' });
      return EMPTY;
    }

    const pinHash = requiresPin
      ? sha256(`${this.pinFormControl.value}-${this.createdUser.id}`).toString()
      : undefined;

    const orgValue = this.organizationFormControl.value;
    const groups = this.userType === UserType.Client
      ? [typeof orgValue !== 'string' ? orgValue?.cognitoGroupUsers : undefined].filter((g): g is string => !!g)
      : getGroupsForUserType(this.userType, environment);

    const userGroups: IUserGroups = {
      pinHash,
      groups,
    };

    return this.usersService.updateUserGroups(this.createdUser.id!, userGroups);
  }

  cancel() {
    if (this.userCreated) {
      this.usersService.deleteUser(this.createdUser).pipe(
        takeUntilDestroyed(this.destroyRef),
      ).subscribe(() => {
        this.dialogRef.close();
      });
    } else {
      this.dialogRef.close();
    }
  }

  onKey(e: any) {
    const key = e.target.value;
    this.timezoneListFiltered = timezoneList.filter((el) => {
      const flag = el.utc.some((el) => el.toLocaleLowerCase().includes(key.toLocaleLowerCase()));
      return flag || el.text.toLocaleLowerCase().includes(key.toLocaleLowerCase());
    });
  }
}
